/**
 * Inject `<base href>` so the slide's relative asset URLs resolve against the
 * bucket it was fetched from rather than the app origin. Required before the
 * HTML goes into an `iframe srcDoc` — srcDoc has no URL of its own, so without
 * this every relative image 404s.
 */
export function withBaseHref(html: string, sourceUrl: string): string {
  return injectIntoHead(html, `<base href="${sourceUrl}">`);
}

/** Insert `tag` right after `<head>`, or at the very start without one. */
export function injectIntoHead(html: string, tag: string): string {
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, (match) => `${match}${tag}`);
  }
  return `${tag}${html}`;
}

export async function fetchSlideHtml(
  sourceUrl: string,
  signal?: AbortSignal,
): Promise<string> {
  const response = await fetch(sourceUrl, { signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch slide content: ${response.status}`);
  }
  return response.text();
}

export function prefetchSlideHtmlAssets(html: string, sourceUrl: string): void {
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return;
  }

  try {
    prefetchSlideDocumentAssets(
      new DOMParser().parseFromString(html, "text/html"),
      sourceUrl,
    );
  } catch {
    // Asset prefetch is best-effort; HTML fetch/cache remains authoritative.
  }
}

/** Same as prefetchSlideHtmlAssets, for a caller that already parsed it. */
export function prefetchSlideDocumentAssets(
  doc: Document,
  sourceUrl: string,
): void {
  if (typeof window === "undefined") return;

  try {
    const imageUrls = new Set<string>();
    doc.querySelectorAll("img[src], image[href]").forEach((node) => {
      const raw =
        node.getAttribute("src") ||
        node.getAttribute("href") ||
        node.getAttribute("xlink:href");
      if (!raw || raw.startsWith("data:")) {
        return;
      }
      try {
        imageUrls.add(new URL(raw, sourceUrl).href);
      } catch {
        // Ignore malformed slide-authored URLs.
      }
    });

    imageUrls.forEach((url) => {
      const image = new Image();
      image.decoding = "async";
      image.src = url;
    });
  } catch {
    // Asset prefetch is best-effort; HTML fetch/cache remains authoritative.
  }
}
